/**
 * A fixed Design's page and its quick add (owner, 1 Nov 2026; docs/DESIGN_TYPES_PLAN.md). The Design is sold as is on its
 * base product: the app signs a bag line for the base variant and the published version the page shows (POST
 * {proxy}/designs/{handle}/line), and the line goes to the cart with that exact set of properties: the visible "Design: <title>",
 * the bag picture and the receipt. Buy now / Shop Pay submit the product form, so the page puts the same signed properties in
 * it first and shows those buttons only once they are ready for the chosen colour. Its gallery's videos behave as a product
 * gallery's (gallery-video.js): muted and looping, play/pause only, only the one in view plays, never for reduced motion.
 */
import { keepVideosMuted, playVideoInView, toggleVideo } from './gallery-video.js';
export const FIXED_STALE = 'This design just changed. Reload the page to see the latest version.';
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const variantGid = id => `gid://shopify/ProductVariant/${String(id).split('/').pop()}`;

/** Whether the app's answer is a fixed-design line for this attempt: Design, picture, `_fl_v` "d1" and complete receipt chunks. */
export function validFixedDesignProperties(properties, requestId) {
  if (!properties || typeof properties !== 'object' || Array.isArray(properties) || properties._fl_v !== 'd1' || properties._fl_attempt !== requestId
    || Object.values(properties).some(value => typeof value !== 'string') || !properties.Design?.trim()
    || !/^https:\/\/[^\s"'<>]+$/.test(properties._fl_design_image || '')) return false;
  const count = Number(properties._fl_receipt_count);
  const chunks = Object.keys(properties).filter(key => /^_fl_receipt_\d+$/.test(key));
  const known = new Set(['Design', '_fl_v', '_fl_attempt', '_fl_design_image', '_fl_receipt_count', ...chunks]);
  return Number.isSafeInteger(count) && count > 0 && String(count) === properties._fl_receipt_count && chunks.length === count
    && chunks.every((_, index) => typeof properties[`_fl_receipt_${index + 1}`] === 'string' && properties[`_fl_receipt_${index + 1}`].length > 0)
    && Object.keys(properties).every(key => known.has(key));
}

/** Asks the app to sign the line; returns its properties. Throws an Error whose message the page shows. */
export async function requestFixedLine({ proxy, handle, version, variantId, intent = 'add', requestId = crypto.randomUUID() }, fetcher = fetch) {
  if (!/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(handle || '') || !Number.isSafeInteger(Number(version)) || !uuid.test(requestId))
    throw Error('Reload this page to add the design to your bag.');
  let response;
  try {
    response = await fetcher(`${proxy}/designs/${handle}/line`, { method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ variantId: variantGid(variantId), version: Number(version), requestId, intent }), signal: AbortSignal.timeout(15000) });
  } catch { throw Error('The design was not confirmed. Please try again.'); }
  const answer = await response.json().catch(() => ({}));
  if (!response.ok) throw Error(response.status === 409 ? FIXED_STALE : answer.error?.message || 'This design could not be added. Please try again.');
  if (answer.schemaVersion !== 'd1' || answer.handle !== handle || answer.version !== Number(version) || answer.variantId !== variantGid(variantId)
    || answer.requestId !== requestId || !validFixedDesignProperties(answer.properties, requestId))
    throw Error('This design could not be confirmed. Reload the page to try again.');
  return answer.properties;
}

/** The bag's picture of the line: the Design's first photo (cart.js rebuilds it from an allow-list), as design-quick-add.js draws it. */
export function bagSnapshot(image) {
  if (/^\/\//.test(image || '')) image = `https:${image}`; // Liquid's image_url is protocol-relative
  if (!/^https:\/\/[^"'<>\s]+$/.test(image || '')) return null;
  return { html: `<div style="width:192px;height:192px"><img src="${image}" style="width:100%;height:100%;object-fit:contain"></div>`, size: 192 };
}

/**
 * Signs and adds one fixed-design line of `quantity`. Dependencies come in so the browser passes the theme's cart modules and
 * tests pass their own. Returns the cart result.
 */
export async function addFixedDesign(card, deps) {
  const { fetcher = fetch, addOne, bagLineId, rememberBagDesign, recordPreorder, root, requestId = crypto.randomUUID() } = deps;
  const properties = await requestFixedLine({ ...card, requestId }, fetcher);
  const variantId = String(card.variantId).split('/').pop();
  const result = await addOne(root, { variantId, properties, quantity: card.quantity || 1 });
  const snapshot = bagSnapshot(card.image);
  if (snapshot && bagLineId(result.line)) rememberBagDesign(bagLineId(result.line), snapshot);
  if (card.preorder && card.shipsOn) { try { result.cart = await recordPreorder(root, { variantId, shipsOn: card.shipsOn }); } catch { /* The design is in the bag. */ } }
  return result;
}

/** The hidden `properties[...]` inputs Buy now and Shop Pay submit with the product form. */
export function propertyInputs(properties) {
  return Object.entries(properties).map(([name, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden'; input.name = `properties[${name}]`; input.value = value;
    return input;
  });
}

const desktop = () => globalThis.matchMedia?.('(min-width: 1024px)').matches;
if (typeof customElements !== 'undefined' && !customElements.get('fl-fixed-design')) {
  customElements.define('fl-fixed-design', class extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-fixed-form]');
      this.variantInput = this.querySelector('[data-fixed-variant]');
      this.add = this.querySelector('[data-fixed-add]');
      this.buyNow = this.querySelector('[data-fixed-buy-now]');
      this.properties = this.querySelector('[data-fixed-properties]');
      this.photos = this.querySelector('[data-fixed-photos]');
      this.quantity = 1; this.prepared = 0; this.signed = new Map();
      this.setupGallery();
      this.addEventListener('click', event => {
        const toggle = event.target.closest('[data-video-toggle]');
        if (toggle) { toggleVideo(toggle); return; }
        const color = event.target.closest('[data-fixed-color]');
        if (color) { this.choose(color); return; }
        const step = event.target.closest('[data-fixed-step]');
        if (step) this.setQuantity(this.quantity + Number(step.dataset.fixedStep));
      });
      this.form?.addEventListener('submit', event => { event.preventDefault(); this.addToBag(); });
      const chosen = this.querySelector('[data-fixed-color][aria-pressed="true"]');
      this.update(chosen);
    }
    setupGallery() {
      if (!this.photos) return;
      const figures = [...this.photos.querySelectorAll('[data-fixed-photo]')];
      const thumbs = [...this.querySelectorAll('[data-fixed-thumb]')];
      let active = 0;
      keepVideosMuted(this.photos);
      // The photo in the row nearest its start edge is the one in view on phones.
      const inRow = () => { const left = this.photos.getBoundingClientRect().left, offset = figure => Math.abs(figure.getBoundingClientRect().left - left);
        return figures.reduce((best, figure, index) => offset(figure) < offset(figures[best]) ? index : best, 0); };
      // Wide screens show one photo with thumbnails beside it; phones swipe through all of them. Only the video in view plays.
      const show = () => { figures.forEach((figure, index) => { figure.hidden = desktop() ? index !== active : false; });
        playVideoInView(figures, desktop() ? active : inRow()); };
      this.photos.dataset.enhanced = '';
      show(); globalThis.matchMedia?.('(min-width: 1024px)').addEventListener?.('change', show);
      let settle = 0;
      this.photos.addEventListener('scroll', () => { if (desktop()) return; globalThis.cancelAnimationFrame(settle); settle = globalThis.requestAnimationFrame(() => playVideoInView(figures, inRow())); }, { passive: true });
      thumbs.forEach(thumb => thumb.addEventListener('click', () => {
        active = Number(thumb.dataset.fixedThumb);
        thumbs.forEach(item => item.setAttribute('aria-pressed', String(item === thumb)));
        show();
      }));
    }
    setQuantity(value) {
      this.quantity = Math.min(99, Math.max(1, value));
      this.querySelector('[data-fixed-quantity]').textContent = String(this.quantity);
      this.querySelector('[data-fixed-quantity-input]').value = String(this.quantity);
    }
    choose(color) {
      this.querySelectorAll('[data-fixed-color]').forEach(item => item.setAttribute('aria-pressed', String(item === color)));
      this.update(color);
    }
    /** The chosen colour: the form's variant, its name, price and button; Buy now waits for its signed line. */
    update(color) {
      if (color) {
        this.variantInput.value = color.dataset.fixedColor;
        const name = this.querySelector('[data-fixed-color-name]'); if (name) name.textContent = color.dataset.colorName;
        this.querySelectorAll('[data-fixed-price]').forEach(price => { price.textContent = color.dataset.price; });
      }
      // One colour has no picker: the button carries the variant's facts.
      const facts = (color || this.add).dataset;
      const available = facts.available === 'true';
      this.available = available;
      this.preorder = facts.preorder === 'true'; this.shipsOn = facts.shipsOn || '';
      const label = this.add.querySelector('[data-fixed-add-label]');
      label.textContent = !available ? 'Sold out' : this.preorder ? 'Pre-order' : 'Add to bag';
      this.add.disabled = !available;
      this.prepareBuyNow();
    }
    async modules() {
      const [cartApi, { routes }] = await Promise.all([import('folded-label/cart-api'), import('folded-label/cart')]);
      return { ...cartApi, root: routes.root, proxy: `${routes.root}${(this.dataset.proxyPath || 'apps/folded-label').replace(/^\/+|\/+$/g, '')}` };
    }
    card(proxy) {
      return { proxy, handle: this.dataset.designHandle, version: this.dataset.designVersion, variantId: this.variantInput.value,
        quantity: this.quantity, image: this.dataset.designImage, preorder: this.preorder, shipsOn: this.shipsOn };
    }
    message(text, error = false) {
      const status = this.querySelector('[data-fixed-status]'), alert = this.querySelector('[data-fixed-error]');
      status.textContent = error ? '' : text; alert.textContent = error ? text : ''; alert.hidden = !error;
      if (error) alert.focus();
    }
    /** Signs ahead for Buy now: once per colour (kept for the page), a moment after the colour settles. Until then Buy now
     *  stays in its place, dimmed and inert, so nothing can check out without the signed line; a sold-out colour or a failed
     *  signing hides it and Add to bag takes the row. */
    async prepareBuyNow() {
      const ticket = ++this.prepared;
      this.properties.replaceChildren(); this.buyNow.inert = true; this.buyNow.classList.add('is-pending');
      this.buyNow.hidden = !this.available;
      if (!this.available) return;
      const variant = this.variantInput.value;
      try {
        let properties = this.signed.get(variant);
        if (!properties) {
          await new Promise(done => setTimeout(done, 300));
          if (ticket !== this.prepared) return;
          const { proxy } = await this.modules();
          properties = await requestFixedLine({ ...this.card(proxy), intent: 'buy_now' });
          this.signed.set(variant, properties);
        }
        if (ticket !== this.prepared) return;
        this.properties.replaceChildren(...propertyInputs(properties));
        this.buyNow.inert = false; this.buyNow.classList.remove('is-pending');
      } catch { if (ticket === this.prepared) this.buyNow.hidden = true; /* Add to bag still works and reports its own problem. */ }
    }
    async addToBag() {
      if (this.add.disabled || this.add.getAttribute('aria-busy') === 'true') return;
      this.add.setAttribute('aria-busy', 'true'); this.message('Adding to your bag…');
      try {
        const modules = await this.modules();
        const result = await addFixedDesign(this.card(modules.proxy), modules);
        if (result.cart) document.dispatchEvent(new CustomEvent('fl:cart-updated', { detail: result.cart }));
        this.message('Added to your bag.');
        document.dispatchEvent(new CustomEvent('fl:open-cart', { detail: { opener: this.add } }));
      } catch (error) { this.message(error.message, true); }
      finally { this.add.removeAttribute('aria-busy'); }
    }
  });
}
